import { LoadingVariant } from '@aurora/shared-client/components/common/Loading/enums';
import Loading from '@aurora/shared-client/components/common/Loading/Loading';
import type { LoadingVariantTypeAndProps } from '@aurora/shared-client/components/common/Loading/types';
import type { SwitchBranchContextInterface } from '@aurora/shared-client/components/context/SwitchBranchContext/SwitchBranchContext';
import SwitchBranchContext from '@aurora/shared-client/components/context/SwitchBranchContext/SwitchBranchContext';
import { clearBranchCookie } from '@aurora/shared-utils/helpers/developer/SwitchBranchCookieHelper';
import usePageLoadingGlobalState from '@aurora/shared-client/helpers/ui/PageLoadingGlobalState';
import { LoadingSize, LoadingSpacing } from '@aurora/shared-client/types/enums';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import type { Props } from './SwitchBranchContextProvider';
import useFloatingBox from '@aurora/shared-client/components/context/FloatingBoxContext/useFloatingBox';
import { SWITCH_BRANCH_FLOATING_BOX_ID } from '../SwitchBranchFloatingBox/SwitchBranchFloatingBox';

const BranchModal = dynamic(
  () =>
    import(
      '@aurora/shared-client/components/developer/ChooseActiveBranchModal/ChooseActiveBranchModal'
    ),
  {
    ssr: false
  }
);

const SwitchBranchFloatingBox = dynamic(
  () => import('../SwitchBranchFloatingBox/SwitchBranchFloatingBox'),
  {
    ssr: false
  }
);

const loadingVariant: LoadingVariantTypeAndProps = {
  type: LoadingVariant.DOT,
  props: {
    size: LoadingSize.LG,
    spacing: LoadingSpacing.XL
  }
};

/**
 * Allows user to switch branches.
 *
 * @author Dolan Halbrook
 */
const SwitchBranchContextProviderMain: React.FC<React.PropsWithChildren<Props>> = ({
  branchName,
  children
}) => {
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [pageLoading] = usePageLoadingGlobalState('pageLoading');
  const { floatingBox, showBox } = useFloatingBox(SWITCH_BRANCH_FLOATING_BOX_ID, {
    initiallyShowBox: branchName && 'main' != branchName
  });

  if (pageLoading) {
    return <Loading variant={loadingVariant} />;
  }

  const context: SwitchBranchContextInterface = {
    branchName: branchName
  };

  return (
    <SwitchBranchContext.Provider value={context}>
      {branchName && (
        <BranchModal
          branchName={branchName}
          showModal={showBranchModal}
          setShowModal={show => setShowBranchModal(show)}
          onBranchChoose={() => {
            window.location.reload();
          }}
        />
      )}
      {showBox &&
        floatingBox(
          <SwitchBranchFloatingBox
            activeBranch={branchName}
            onSwitchToBranch={() => {
              setShowBranchModal(true);
            }}
            onSwitchToMainBranch={async () => {
              clearBranchCookie();
              window.location.reload();
            }}
          />
        )}
      {children}
    </SwitchBranchContext.Provider>
  );
};

export default SwitchBranchContextProviderMain;
